// src/DetailsView.jsx
import React, { useEffect, useState, useRef } from 'react';
import { signInWithRedirect } from 'aws-amplify/auth';
import { useSession } from './auth/SessionProvider.jsx';
import { api } from './lib/apiFetch';

export default function DetailsView({ item, onClose }) {
  const { signedIn } = useSession();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageUrls, setImageUrls] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadedCount, setLoadedCount] = useState(0);
  const [error, setError] = useState('');

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Strip the legacy "nonopt." prefix (without mutating source)
  const normalizeKey = (k = '') => (k.startsWith('nonopt.') ? k.slice('nonopt.'.length) : k);

  useEffect(() => {
    const imgs = Array.isArray(item?.images) ? item.images : [];
    if (!imgs.length || !signedIn) {
      setImageUrls([]);
      setLoadedCount(0);
      setIsLoading(false);
      setError(signedIn ? '' : 'Please sign in to view images.');
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setLoadedCount(0);
    setError('');
    setCurrentIndex(0);

    (async () => {
      try {
        const promises = imgs.map(async (rawKey) => {
          const imageKey = normalizeKey(rawKey);
          // Backend: POST /getImage { imageKey } → { image: base64String }
          const data = await api.post('/getImage', { imageKey });
          if (cancelled || !mountedRef.current) return null;

          // count as loaded even if missing
          setLoadedCount((c) => c + 1);

          if (!data?.image) return null;
          return `data:image/jpeg;base64,${data.image}`;
        });

        const results = await Promise.all(promises);
        const urls = results.filter(Boolean);
        if (!cancelled && mountedRef.current) setImageUrls(urls);
      } catch (e) {
        if (!cancelled && mountedRef.current) {
          setError(String(e?.message || e));
          setImageUrls([]);
        }
      } finally {
        if (!cancelled && mountedRef.current) setIsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [item?.images, signedIn]);

  const next = () =>
    setCurrentIndex((prev) => (imageUrls.length ? Math.min(prev + 1, imageUrls.length - 1) : 0));

  const prev = () =>
    setCurrentIndex((prev) => (imageUrls.length ? Math.max(0, prev - 1) : 0));

  const handleSaveImage = () => {
    if (!imageUrls.length) return;
    const link = document.createElement('a');
    link.href = imageUrls[currentIndex];
    link.download = `Image_${currentIndex + 1}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Keep the image at a fixed height while preserving aspect ratio
  const handleImageLoad = (e) => {
    const h = 300;
    const { naturalWidth: w0, naturalHeight: h0 } = e.target;
    if (!w0 || !h0) return;
    const w = (h * w0) / h0;
    e.target.style.height = `${h}px`;
    e.target.style.width = `${w}px`;
  };

  const total = Array.isArray(item?.images) ? item.images.length : 0;
  const progressPct = total ? Math.min(100, Math.round((loadedCount / total) * 100)) : 0;

  return (
    <div className="modal-backdrop">
      <div className="modal-content">
        <button className="modal-close" onClick={onClose} aria-label="Close details">×</button>

        <div className="container modal-group">
          <div className="detail-data">
            <div className="details-row"><strong>Unit Number:</strong> {item?.unitNumber || '—'}</div>
            <div className="details-row"><strong>Cleaning Type:</strong> {item?.cleaningType || '—'}</div>
            <div className="details-row"><strong>Cleaned By:</strong> {item?.cleanedBy || '—'}</div>
          </div>
          <div className="detail-data">
            <div className="details-row"><strong>Date Cleaned:</strong> {item?.dateCleaned || '—'}</div>
            <div className="details-row"><strong>Price:</strong> {item?.price ? `$${item.price}` : '—'}</div>
            <div className="details-row"><strong>Paid:</strong> {item?.paid || '—'}</div>
          </div>
        </div>

        <textarea className="scrollable-textbox" value={item?.cleaningNotes || ''} readOnly />

        <div className="image-slideshow-container">
          {!signedIn && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ color: 'crimson', marginBottom: 6 }}>{error || 'Please sign in to view images.'}</div>
              <button className="link-btn" onClick={() => signInWithRedirect()}>Sign in</button>
            </div>
          )}

          {signedIn && error && (
            <div style={{ color: 'crimson', marginBottom: 8 }}>{error}</div>
          )}

          {signedIn && (isLoading ? (
            <div className="progress-container" style={{ width: '100%' }}>
              <div
                style={{
                  width: `${progressPct}%`,
                  height: 20,
                  backgroundColor: 'green',
                  transition: 'width 0.3s ease-in-out',
                }}
              />
              <div style={{ marginTop: 6 }}>
                Loading {Math.min(loadedCount, total)}/{total}
              </div>
            </div>
          ) : !imageUrls.length ? (
            <div style={{ margin: '12px 0' }}>No images available.</div>
          ) : (
            <>
              <button
                className="carousel-control left"
                onClick={prev}
                disabled={currentIndex === 0}
                aria-label="Previous image"
              >
                {'<'}
              </button>

              <img
                src={imageUrls[currentIndex]}
                alt={`Detail ${currentIndex + 1}`}
                className="displayed-image"
                onLoad={handleImageLoad}
              />

              <div className="image-index">{currentIndex + 1}/{imageUrls.length}</div>

              <button
                className="carousel-control right"
                onClick={next}
                disabled={currentIndex >= imageUrls.length - 1}
                aria-label="Next image"
              >
                {'>'}
              </button>
            </>
          ))}

          <button className="save-button" onClick={handleSaveImage} disabled={!imageUrls.length || !signedIn}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
